import _ from 'lodash';
import { action, computed, observable } from 'mobx';
import MarginType, { convertMarginTypeResponseToArgs } from '@this/domain/organization/margin_type3';
import moment from 'moment';
import type { MarginTypeResponseArgs } from '@this/domain/organization/margin_type3';
import Hotel from '../hotel/hotel2';
import Transit from '../transit/transit2';
import type NonOrderItem from '../non_order_item2';
import type { SelectType } from '../select_store';
import ReservingTripItem, {
  convertReservingTripItemResponseToArgs,
  type ReservingTripItemResponseArgs
} from './reserving_trip_item2';
import type { RentalCarResponseArgs } from '../rental_car';
import RentalCar, { convertRentalCarResponseToArgs } from '../rental_car';
import TravelerInformation, { convertTravelerInformationResponseToArgs } from '../traveler_information';
import type { TravelerInformationResponseArgs } from '../traveler_information';
import type Organization from '../organization/organization3';
import type ReserveInfo from '../reserve_info2';
import type AirRouteDetail from '../flight/air_route_detail';
import Flight from '../flight/flight2';

export type ReservingTripResponseArgs = {
  type?: SelectType;
  items: ReservingTripItemResponseArgs[];
  rental_cars?: RentalCarResponseArgs[];
  car_type: [string, string][];
  travelers: TravelerInformationResponseArgs[];
  margin_types: MarginTypeResponseArgs[];
  airline?: 'ANA' | 'JAL';
  show_fee?: boolean;
};

export type ReservingTripArgs = {
  packageType?: SelectType;
  items?: ReservingTripItem[];
  rentalCars?: RentalCar[];
  nonOrderItems?: NonOrderItem[];
  peopleNum?: number;
  showFee?: boolean;
  marginTypes?: MarginType[];
  carTypeOptions: [string, string][];
  travelers: TravelerInformation[];
  parentReserveInfo?: ReserveInfo;
  airline?: 'ANA' | 'JAL';
};

export const convertReservingTripResponseToArgs = (response: ReservingTripResponseArgs) => {
  const args: ReservingTripArgs = {
    packageType: response.type,
    items: response.items.map(raw => new ReservingTripItem(convertReservingTripItemResponseToArgs(raw))),
    rentalCars: response.rental_cars?.map(raw => new RentalCar(convertRentalCarResponseToArgs(raw))),
    carTypeOptions: response.car_type,
    travelers: (response.travelers ?? []).map(
      raw => new TravelerInformation(convertTravelerInformationResponseToArgs(raw))
    ),
    marginTypes: response.margin_types.map(raw => new MarginType(convertMarginTypeResponseToArgs(raw))),
    airline: response.airline,
    showFee: response.show_fee
  };
  return args;
};

export default class ReservingTrip {
  @observable packageType: SelectType | undefined;
  @observable items: ReservingTripItem[];
  @observable nonOrderItems: NonOrderItem[];
  @observable peopleNum: number;
  @observable showFee?: boolean;
  @observable marginTypes?: MarginType[];
  @observable rentalCars: RentalCar[];
  @observable carTypeOptions: [string, string][];
  @observable travelers: TravelerInformation[];
  @observable parentReserveInfo?: ReserveInfo;
  @observable airline?: 'ANA' | 'JAL';

  constructor(args?: ReservingTripArgs) {
    this.packageType = args?.packageType;
    this.items = args?.items || [];
    this.items.forEach(item => {
      item.parentTrip = this;
    });
    this.nonOrderItems = args?.nonOrderItems || [];
    this.peopleNum = args?.peopleNum || 1;
    this.showFee = args?.showFee;
    this.marginTypes = args?.marginTypes;
    this.rentalCars = args?.rentalCars ?? [];
    this.carTypeOptions = args?.carTypeOptions ?? [];
    this.travelers = args?.travelers ?? [];
    this.parentReserveInfo = args?.parentReserveInfo;
    this.airline = args?.airline;
  }

  @computed get organization(): Organization | undefined {
    return this.parentReserveInfo?.organization;
  }

  @computed get isPackage() {
    return this.packageType === 'airPackage' || this.packageType === 'jrPackage';
  }

  @computed get isForeignFlightCustomerInfoRequired() {
    return this.items.some(item => !!item && item.isFlight);
  }

  @computed get itemsAirExists() {
    return this.items.some(item => !!item && !!item.airExists);
  }

  @computed get itemsShinkansenExists() {
    return this.items.some(item => !!item && item.shinkansenExists);
  }

  @computed get itemsExpressExists() {
    return this.items.some(item => !!item && item.expressExists);
  }

  @computed get itemsHotelExists() {
    return this.items.some(item => !!item && item.isHotel);
  }

  @computed get itemsDomesticExists() {
    return this.items.some(item => !!item && item.isTransit && item.isDomesticExist);
  }

  @computed get isDomesticFlightCustomerInfoRequired() {
    return this.itemsAirExists && this.itemsDomesticExists;
  }

  @computed get isFlightCustomerInfoRequired() {
    return this.itemsAirExists;
  }

  @computed get isHotelInfoRequired() {
    return this.itemsHotelExists;
  }

  @computed get isShinkansenInfoRequired() {
    return this.shinkansenExists || this.expressExists;
  }

  @computed get shinkansenExists() {
    return this.itemsShinkansenExists;
  }

  @computed get expressExists() {
    return this.itemsExpressExists;
  }

  @computed get totalPrice() {
    if (this.isPackage) {
      return this.packageTotalPrice * this.peopleNum;
    }
    return this.itemsTotalPrice;
  }

  @computed get packageTotalPrice() {
    const price = this.packagePrice;
    return price ? price + this.packageMargin : 0;
  }

  @computed get packagePrice() {
    return this.packageBasePrice + this.packageOutwardPrice + this.packageHomewardPrice;
  }

  @computed get packageBasePrice() {
    const hotel = this.items
      .map(item => item && item.element)
      .find<Hotel>((element): element is Hotel => element instanceof Hotel);

    return hotel?.packageBasePrice || 0;
  }

  @computed private get packageTransits() {
    const transits = this.items
      .map(item => item && item.element)
      .filter<Transit>((element): element is Transit => element instanceof Transit);
    transits.sort(
      (a, b) =>
        new Date(a.segments[0].legs[0].from.timeFull).getTime() -
        new Date(b.segments[0].legs[0].from.timeFull).getTime()
    );

    return transits;
  }

  @computed get packageOutwardPrice() {
    const [outword] = this.packageTransits;
    if (outword === undefined) return 0;
    return outword.additionalPrice || 0;
  }

  @computed get packageHomewardPrice() {
    const [, homeword] = this.packageTransits;
    if (homeword === undefined) return 0;
    return homeword.additionalPrice || 0;
  }

  @computed get packageMargin() {
    if (!this.showFee) {
      return 0;
    }
    if (!this.marginTypes) {
      return 0;
    }
    const marginType = this.marginTypes.find(m => m.category === 'domestic_package');
    if (!marginType) {
      return 0;
    }
    return marginType.calcMarginAmount(this.packagePrice, 1);
  }

  @computed get itemsTotalPrice() {
    return this.items.reduce((sum, item) => {
      if (!item) return sum;
      const price = item.totalPrice;
      return sum + (price || 0);
    }, 0);
  }

  @computed get restrictedHotels() {
    const hotels = this.items
      .map(item => item && item.element)
      .filter<Hotel>((element): element is Hotel => element instanceof Hotel);
    return hotels.filter(hotel => hotel.selectedPlanRestricted);
  }

  @computed get groupedRestrictedHotels() {
    return _.groupBy(this.restrictedHotels, hotel => hotel.name);
  }

  @computed get isUnreservedSeatAvailable() {
    return this.items.some(item => item?.isUnreservedSeatAvailable);
  }

  @computed get startDate(): string | undefined {
    let dates = _.compact(_.map(this.items, item => item && item.startDate));
    if (dates.length === 0) {
      dates = _.compact(_.map(this.rentalCars, car => car && car.departureTime.format('YYYY-MM-DD')));
    }
    return _.first(dates.sort());
  }

  @computed get endDate(): string | undefined {
    let dates = _.compact(_.map(this.items, item => item && item.endDate));
    if (dates.length === 0) {
      dates = _.compact(_.map(this.rentalCars, car => car && car.returnTime.format('YYYY-MM-DD')));
    }
    return _.last(dates.sort());
  }

  @computed get packageTotalBufferAmount() {
    return this.organization?.getPackageBufferAmount() || 0;
  }

  @computed get itemsTotalBufferAmount() {
    if (this.isPackage) {
      return this.packageTotalBufferAmount * this.peopleNum;
    }
    const prices = _.map(this.items, item => (item && item.totalBufferAmount) || 0);
    return _.sum(prices);
  }

  @action addRentalCar() {
    this.rentalCars.push(
      new RentalCar({
        departureTime: moment(this.startDate || ''),
        departureTimeType: 0,
        returnTime: moment(this.endDate || ''),
        returnTimeType: 0,
        departurePlace: '',
        returnPlace: 'current',
        returnPlaceName: '',
        carType: 'compact',
        carTypeOptions: this.carTypeOptions,
        carTypeOther: '',
        carNumber: 1,
        remarks: '',
        firstNameKana: this.travelers[0].firstNameKana || '',
        lastNameKana: this.travelers[0].lastNameKana || '',
        driverTel: this.travelers[0].flightTel || ''
      })
    );
  }

  @action removeRentalCar(i: number) {
    this.rentalCars = this.rentalCars.filter((r, ri) => ri !== i);
  }

  @computed get totalPriceWithBuffer() {
    return this.totalPrice + this.itemsTotalBufferAmount;
  }

  @computed get hasLegSummaries(): boolean {
    let exists = false;
    this.items.forEach(item => {
      if (item?.element instanceof Transit && item?.element.legsummariesFromtoJrair) {
        exists = true;
      }
    });
    return exists;
  }

  @computed get tooltipPackageText(): string {
    let text = `\
パッケージ：${utils.formatPrice(this.packageTotalPrice)} × ${this.peopleNum}名
└ 基本料金：${utils.formatPrice(this.packageBasePrice)}
└ 往路追加料金：${utils.formatPrice(this.packageOutwardPrice)}
└ 復路追加料金：${utils.formatPrice(this.packageHomewardPrice)}\
`;
    const margin = this.packageMargin;
    if (margin > 0) {
      text += `\n└ 手配手数料：${utils.formatPrice(this.packageMargin)}`;
    }
    return text;
  }

  @computed get itemsPriceTexts(): string[] {
    return _.map(this.items, item => (item && item.priceText) || '');
  }

  @computed get tooltipText(): string {
    return this.isPackage ? this.tooltipPackageText : this.itemsPriceTexts.join('\n');
  }

  @computed get totalPriceShortDistance(): number {
    let price = 0;
    this.items.forEach(item => {
      if (
        item?.element instanceof Transit &&
        item?.element.legsummariesFromtoBefore &&
        item?.element.legsummariesTypepriceBefore?.price
      ) {
        price += item.element.legsummariesTypepriceBefore.price;
      }
      if (
        item?.element instanceof Transit &&
        item?.element.legsummariesFromtoAfter &&
        item?.element.legsummariesTypepriceAfter?.price
      ) {
        price += item.element.legsummariesTypepriceAfter.price;
      }
    });
    return price;
  }

  @computed get isAirPackage(): boolean {
    return this.packageType === 'airPackage';
  }

  @computed get isAnaAirPackage(): boolean {
    return this.isAirPackage && this.airline === 'ANA';
  }

  @computed get isJalAirPackage(): boolean {
    return this.isAirPackage && this.airline === 'JAL';
  }

  @computed get airRouteDetail(): AirRouteDetail | undefined {
    const item: ReservingTripItem | undefined = this.items.find(itm => !!itm && itm.isFlight);

    if (!item) {
      return undefined;
    }

    // air_route_detailは日本→海外のみ取得できるため
    if (!(item.element instanceof Flight)) {
      return undefined;
    }

    const segment = item.element.outword?.segments[0];
    return segment?.airRouteDetail;
  }

  static anaPackageDesc() {
    return (
      '楽天ANAトラベルオンライン株式会社\n' +
      '東京都世田谷区玉川一丁目14番1号 楽天クリムゾンハウス\n' +
      '観光庁長官登録旅行業　第1810号\n' +
      '一般社団法人日本旅行業協会正会員\n'
    );
  }

  static jalPackageDesc() {
    return (
      '株式会社JALパック\n' +
      '東京都品川区東品川2-4-11 野村不動産天王洲ビル\n' +
      '観光庁長官登録旅行業　第705号\n' +
      '一般社団法人日本旅行業協会正会員\n'
    );
  }

  static consignmentSaleDesc(tel: string) {
    return (
      '株式会社トランスファーデータ\n' +
      '東京都港区芝四丁目７番８号芝ワカマツビル８階\n' +
      '東京都知事登録旅行業第2-7247号\n' +
      '一般社団法人日本旅行業協会正会員\n' +
      '営業時間：平日9時-18時\n' +
      `連絡先　：${tel}\n` +
      '\n' +
      '総合旅行業務取扱管理者\n' +
      '澁谷 誠\n' +
      '\n' +
      '総合旅行業務取扱管理者とは、お客様の旅行を扱う\n' +
      '旅行会社・営業所での取引に関する責任者です。\n' +
      'この旅行契約に関し、担当者からの説明にご不明な点があれば、ご遠慮なく取扱管理者におたずねください。\n'
    );
  }
}
